'use client';

import React, { useMemo } from 'react';
import { useLayoutControl } from 'src/context/layout';
import { useProfile } from 'src/context/providers/ProfileProvider';
import Link from 'next/link';
import { exists } from 'src/utils/exists/exists';
import { formatPhoneNumber } from 'src/utils/formatPhoneNumber/formatPhoneNumber';
import { createGoogleMapsUrl } from 'src/utils/createGoogleMapsLink';
import { ReportDataIssueButton } from 'src/modules/Profile/components/Dialogs';
import { Box, ProfileCardHeader, Card, CircularProgress, Typography } from 'src/components/PulseUI';

const traumaLabelMap = {
  1: 'Level I',
  2: 'Level II',
  3: 'Level III',
  4: 'Level IV',
  5: 'Level V',
};

const additionalDetailsMap = {
  alos_days: 'Average Length of Stay',
  operating_room_count: 'Operating Rooms',
  total_bed_count: 'Staffed Beds',
  mac_carrier: 'Medicare Administrative Contractor (MAC)',
  adult_trauma_level: 'Adult Trauma',
  pediatric_trauma_level: 'Pediatric Trauma',
  group_purchasing_orgs: 'Group Purchasing Organization (GPO)',
};

// Define the desired order of titles explicitly
const orderedDetailTitles = [
  'Type of Service',
  'CMS Provider ID',
  'Name',
  'Address',
  'City/State',
  'Phone',
  'Website',
  'Average Length of Stay',
  'Operating Rooms',
  'Staffed Beds',
  'Medicare Administrative Contractor (MAC)',
  'Adult Trauma',
  'Pediatric Trauma',
  'Group Purchasing Organization (GPO)',
];

const ProfileCenterDetails = () => {
  const { entityDetails, isLoading } = useProfile();
  const { setShowReportDataIssueDialog } = useLayoutControl();

  const profileId = entityDetails?.provider_id;
  const profileType = entityDetails?.type;

  // Calculate centerDetails regardless of loading state to avoid conditional hook calls
  const centerDetails = useMemo(() => {
    // Initialize map with null values based on the desired order
    const detailsMap = new Map<string, any>(
      orderedDetailTitles.map((title) => [title, null])
    );

    // --- Populate values, prioritizing entityDetails where specified ---

    // 1. Name (from entityDetails.name)
    if (entityDetails?.name) {
      detailsMap.set('Name', entityDetails.name);
    }

    // 2. Address (from entityDetails.primary_address)
    const primaryAddress = entityDetails?.primary_address;
    if (primaryAddress) {
      const line1 = primaryAddress.address_line_1;
      const line2 = primaryAddress.address_line_2;
      // Combine lines with a space if both exist, otherwise use line1
      const addressValue = [line1, line2].filter(Boolean).join(' ');
      if (addressValue) {
        detailsMap.set('Address', addressValue);
      }

      // 3. City/State (from entityDetails.primary_address)
      const city = primaryAddress.city;
      const state = primaryAddress.state;
      if (city && state) {
        detailsMap.set('City/State', `${city}, ${state}`);
      }

      // 4. Phone (from entityDetails.primary_address.address_practice_phone)
      const entityPhone = primaryAddress.phone;
      if (entityPhone) {
        const formattedEntityPhone = formatPhoneNumber(entityPhone);
        if (formattedEntityPhone) {
          detailsMap.set('Phone', formattedEntityPhone);
        }
      }
    }

    // 5. Populate Type of Service, CMS Provider ID, and Website from entityDetails
    if (entityDetails?.specialty) {
      detailsMap.set('Type of Service', entityDetails.specialty);
    }

    if (entityDetails?.id) {
      detailsMap.set('CMS Provider ID', entityDetails.id);
    }

    if (entityDetails?.website) {
      detailsMap.set('Website', entityDetails.website);
    }

    // 6. Populate fields from additionalDetailsMap using entityDetails
    Object.entries(additionalDetailsMap).forEach(([key, title]) => {
      if (detailsMap.get(title) !== null) return;

      const rawValue =
        key === 'group_purchasing_orgs'
          ? entityDetails?.gpos
              ?.map((gpo) => gpo.name)
              .filter((name) => name)
              .join(', ')
          : entityDetails?.[key];

      if (!exists(rawValue) || rawValue === '') return;

      const formatters = {
        adult_trauma_level: (val) => traumaLabelMap[val] ?? null,
        pediatric_trauma_level: (val) => traumaLabelMap[val] ?? null,
        alos_days: (val) => `${parseFloat(val)?.toFixed(1)} days`,
        operating_room_count: (val) => String(val),
        total_bed_count: (val) => String(val),
      };

      const processedValue = formatters[key]?.(rawValue) ?? rawValue;

      if (processedValue !== null) {
        detailsMap.set(title, processedValue);
      }
    });

    // Convert map back to array, respecting the initial order
    const finalDetails = orderedDetailTitles.map((title) => ({
      title,
      value: detailsMap.get(title),
    }));

    return finalDetails;
  }, [entityDetails]);

  // Show loading state if data is still loading
  if (isLoading) {
    return (
      <Card className="p-6">
        <Box className="flex justify-center items-center p-4">
          <CircularProgress size="sm" />
        </Box>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <ProfileCardHeader
        icon={profileType}
        title="Details"
        infoTooltip={true}
        tooltipView="centerProfile"
      />
      <Box className="flex flex-col gap-2">
        {centerDetails?.map((item) => {
          const isWebsite = item.title === 'Website';
          const isAddress = item.title === 'Address';
          const formattedTitle = item.title;
          const formattedValue = item.value;
          const notAvailableText = 'Not available';

          // Find the city/state value for the Google Maps URL
          const cityStateItem = centerDetails.find(
            (detail) => detail.title === 'City/State'
          );
          const cityStateValue = cityStateItem?.value || '';

          // Create Google Maps URL for address
          const mapsUrl =
            isAddress && formattedValue
              ? createGoogleMapsUrl(formattedValue, cityStateValue)
              : '';

          const key = item.title;

          return (
            <Box key={key} className="flex flex-col">
              <Typography variant="body2" color="tertiary">
                {formattedTitle}
              </Typography>
              <Typography variant="body2" color="primary" bold>
                {isWebsite &&
                typeof formattedValue === 'string' &&
                formattedValue ? (
                  <Link
                    href={
                      formattedValue.startsWith('http')
                        ? formattedValue
                        : `http://${formattedValue}`
                    }
                    scroll={false}
                    className="text-link block overflow-hidden text-ellipsis whitespace-nowrap max-w-full"
                  >
                    {formattedValue}
                  </Link>
                ) : isAddress &&
                  typeof formattedValue === 'string' &&
                  formattedValue ? (
                  <Link
                    href={mapsUrl}
                    scroll={false}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link"
                    aria-label={`View ${formattedValue} on Google Maps`}
                  >
                    {formattedValue}
                  </Link>
                ) : Array.isArray(formattedValue) &&
                  formattedValue.length === 0 ? (
                  notAvailableText
                ) : Array.isArray(formattedValue) ? (
                  formattedValue.map((value, index) => (
                    <Box key={index} className="flex flex-row gap-2">
                      <Typography variant="body2" color="primary" bold>
                        {value || notAvailableText}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  formattedValue || notAvailableText
                )}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <ReportDataIssueButton
        onClick={() =>
          setShowReportDataIssueDialog({
            show: true,
            providerId: profileId,
            providerType: entityDetails?.type || 'center',
          })
        }
      />
    </Card>
  );
};

export default ProfileCenterDetails;
